import { useAuth0 } from '@auth0/auth0-react';
import { useQuery } from '@tanstack/react-query';

import { API_URL, apiFetch } from '@/config/api';
import { parseError } from '@/hooks/useTeams';

import type {
  DashboardPeriod,
  SlackDashboard,
  SlackDeliverySlo,
  SlackInstallationListing,
} from './slack-bot.types';

const QUERY_KEY = ['slack-bot'] as const;
export const NEEDS_ADMIN =
  'This page needs the admin permissions (read:admin and manage:feedback) on your account.';

// A missing permission won't come back on a retry; anything else may
const retryUnlessRefused = (failures: number, error: Error): boolean =>
  error.message !== NEEDS_ADMIN && failures < 3;

function useSlackFetch() {
  const { getAccessTokenSilently } = useAuth0();
  return async <T>(path: string): Promise<T> => {
    const response = await apiFetch(
      `${API_URL}/admin/slack/installations${path}`,
      {
        headers: {
          Authorization: `Bearer ${await getAccessTokenSilently()}`,
        },
      }
    );
    if (response.status === 403) throw new Error(NEEDS_ADMIN);
    if (!response.ok) throw new Error(await parseError(response));
    return response.json() as Promise<T>;
  };
}

export function useSlackInstallsQuery() {
  const slackFetch = useSlackFetch();
  return useQuery({
    queryKey: [...QUERY_KEY, 'installs'],
    retry: retryUnlessRefused,
    queryFn: async () =>
      (await slackFetch<{ installations: SlackInstallationListing[] }>(''))
        .installations,
  });
}

export function useSlackDashboardQuery(
  installId: string | null,
  period: DashboardPeriod
) {
  const slackFetch = useSlackFetch();
  return useQuery({
    queryKey: [...QUERY_KEY, 'dashboard', installId, period],
    queryFn: () =>
      slackFetch<SlackDashboard>(`/${installId}/dashboard?period=${period}`),
    enabled: installId !== null,
    // The last range's figures stand in for the next range's, and are marked.
    // Never another workspace's, which would read as this one's
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === installId ? previous : undefined,
    retry: retryUnlessRefused,
  });
}

export function useSlackDeliveryQuery(installId: string | null) {
  const slackFetch = useSlackFetch();
  return useQuery({
    queryKey: [...QUERY_KEY, 'delivery', installId],
    queryFn: () => slackFetch<SlackDeliverySlo>(`/${installId}/slo`),
    enabled: installId !== null,
    retry: retryUnlessRefused,
  });
}
